import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  effect,
  ElementRef,
  EventEmitter,
  HostListener,
  inject,
  input,
  Input,
  model,
  ModelSignal,
  NgZone,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  signal,
  Type,
  untracked,
  ViewChild,
  ViewChildren,
  WritableSignal,
} from '@angular/core';
import {UiHelperService} from '../../ui-helper/ui-helper.service';
import {
  BodyScrollEvent,
  CellClickedEvent,
  CellContextMenuEvent,
  CellDoubleClickedEvent,
  CellMouseDownEvent,
  CellPosition,
  CellValueChangedEvent,
  ColDef,
  ColumnApi,
  ColumnResizedEvent,
  FirstDataRenderedEvent,
  GetRowIdFunc,
  GridApi,
  GridOptions,
  IDatasource,
  IGetRowsParams,
  RowClickedEvent,
  RowNode,
  VirtualColumnsChangedEvent,
} from 'ag-grid-community';
import {IGridReady} from './interfaces/grid-ready.interface';
import {SharedGridOptions} from './shared-gridoptions.const';
import {TableApiAdapter} from '../adapters/table/table-api-adapter';
import {ITableAction} from './interfaces/actions.interface';
import {DEFAULT_TABLE_ACTIONS} from './default-table-actions.constant';
import {
  BehaviorSubject,
  combineLatest,
  distinctUntilChanged,
  EMPTY,
  forkJoin,
  merge,
  Observable,
  of,
  switchMap,
  timer
} from 'rxjs';
import {
  catchError,
  debounce,
  debounceTime,
  filter,
  finalize,
  map,
  mergeMap,
  take,
  takeUntil,
  tap
} from 'rxjs/operators';
import {ITableControlFilter} from './filter.interface';
import {IFilter} from '../../../../../../nucleus/src/lib/control/interfaces/filter.interface';
import {ICustomColDef} from './interfaces/custom-col-def.interface';
import {NgbDate} from '@ng-bootstrap/ng-bootstrap';
import {sameArrayValues} from './same-array-values.function';
import {ColTypeColDefMap} from './col-type-col-def.map';
import {ViewPort, ViewPortTable} from './viewport';
import {EControlActions, SignalControl, StoreService, TableControlService, TableService} from 'frontier/nucleus';
import {DisplayedColumns} from './interfaces/displayed-columns.interface';
import {IApiRow} from './api-row.interface';
import {RowEvent} from "ag-grid-community/dist/lib/events";
import {AgGridAngular} from "ag-grid-angular";
import {Clipboard} from '@angular/cdk/clipboard';
import {MatDialog} from "@angular/material/dialog";
import {IApiTableData} from './interfaces/api-table-data.interface';
import {ICustomGridRow} from './interfaces/custom-grid-row.interface';
import {Column} from 'ag-grid-community/dist/lib/entities/column';
import {IColumnFilter} from './interfaces/column-filter.interface';
import {isFilterExpressionCompatible} from './filter-type-compatibility';
import {DomSanitizer} from '@angular/platform-browser';
import {patchState} from '@ngrx/signals';
import {FeedbackService} from '../../../services/feedback.service';
import {ClipboardService} from '../../../services/clipboard.service';
import {IPdfBlob} from './pdf-blob-event.interface';
import {HttpResponse} from "@angular/common/http";
import {NewEntryDialogComponent, OnTabActivated, ToolbarStore} from 'frontier/browserkit';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {TabActivatedDirective} from 'frontier/browserkit/src/lib/directives/tab-activated.directive';
import {SelectionChange, SelectionModel} from '@angular/cdk/collections';
import {EditRowDialogComponent} from '../../../dialogs/basics/edit-row-dialog/edit-row-dialog.component';
import {INewLineParams} from './interfaces/new-line-params.interface';

interface IApiSortModel {
  sortby?: string[];
  direction?: (0 | 1)[];
}

interface IReducedColumnState {
  sortIndex: number;
  colId: string | undefined;
  sort: string | null | undefined;
}

export interface IRowDataChange {
  selected: ICustomGridRow[];
  all: ICustomGridRow[];
}

@Component({
  selector: 'app-table-control',
  templateUrl: './table-control.component.html',
  styleUrls: ['./table-control.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TableControlComponent extends SignalControl<IApiTableData, void>
  implements OnInit, OnDestroy, AfterViewInit, OnTabActivated {
  private _toolbarService = inject(ToolbarStore)
  protected _tableService: TableService = inject(TableService);
  protected _tableControlService: TableControlService = inject(TableControlService);
  protected _tabActivatedDirective = inject(TabActivatedDirective)

  protected readonly feedbackService: FeedbackService = inject(FeedbackService);
  public readonly cdr = inject(ChangeDetectorRef);
  protected readonly store = inject(StoreService);
  protected readonly dialog = inject(MatDialog);
  protected readonly clipboard = inject(Clipboard);
  protected readonly clipboardService = inject(ClipboardService);
  protected readonly zone = inject(NgZone);
  protected readonly sanitizer = inject(DomSanitizer);
  protected readonly elementRef: ElementRef = inject(ElementRef);
  protected readonly uiHelperService: UiHelperService = inject(UiHelperService);

  /**
   * Optional help content. When a subclass sets this, a help icon is shown at the
   * right edge of the toolbar and opens this component in the side help panel.
   */
  public helpComponent: Type<unknown> | null = null;

  /** Opens the configured help component in the side help panel. */
  public openHelpPanel(): void {
    if (this.helpComponent) {
      this.uiHelperService.openHelp({component: this.helpComponent});
    }
  }


  private _startSelectIndex: number;
  private _endSelectIndex: number;
  readonly _rowSelection: SelectionModel<string> = new SelectionModel(true);
  selectedRows: WritableSignal<ICustomGridRow[]> = signal<ICustomGridRow[]>([]);

  manualResizedColumns: Set<string> = new Set<string>();

  // Tracks whether a splitter is being dragged so that the table's overlay doesn't catch the cursor events
  isDragging: WritableSignal<boolean> = signal(false);

  tableName: string;
  newLineParams?: INewLineParams;
  gridApi: GridApi;
  readonly gridApiSig = signal<GridApi>(null);
  colApi: ColumnApi;
  columnDefs: ColDef[];
  apiCols: DisplayedColumns[];

  @ViewChild(AgGridAngular) gridRef: AgGridAngular;
  @ViewChildren('grid', {read: ElementRef}) gridElementRefs: QueryList<ElementRef>;
  @ViewChild('gridWrapper', {read: ElementRef}) gridWrapperRef: ElementRef;
  @ViewChild('fileUpload') fileUploadRef: ElementRef;
  @ViewChild('pdfOverlay', {read: ElementRef}) pdfOverlayRef: ElementRef;
  // This is a backup variable. In the first line the Windows clipboard is used for Copy and Paste. This variable is
  // accessed where the Windows Variable would convert the copied value or when browsers/browser versions are used,
  // that do not support the Paste implementation from Windows clipboard.
  clipboardValue: any;

  /**
   * Grid options of the ag grid component. If you need additional options, you can inherit the SharedGridOptions
   * and add properties, or you can also pass your own grid options.
   */
  @Input() gridOptions: GridOptions = {
    ...SharedGridOptions,
    // SharedGridOptions is spread shallowly, meaning nested objects like defaultColDef still share the same reference
    // across all table instances. AG Grid mutates these objects internally, causing all tables to briefly display the
    // column definitions of whichever table completes its API request first. Spreading defaultColDef here ensures each
    // instance receives its own independent copy.
    defaultColDef: {...SharedGridOptions.defaultColDef},
  };

  @Input() gridClass = 'ag-theme-alpine';

  /**
   * The adapter that the table uses to map api rows to ag grid rows.
   * If some column properties have to be added or edited before the columns are passed to the ag grid,
   * you can create another Table api adapter to adapt necessary changes.
   */
  @Input() apiAdapter: TableApiAdapter = new TableApiAdapter(
    {},
    ColTypeColDefMap
  );

  @Input() maxAutoColWidth: number = 300;

  private _fixedPageSize: number;
  @Input() set fixedPageSize(pageSize: number) {
    this._fixedPageSize = pageSize;
    this.setPaginationPageSize();
  };

  get fixedPageSize() {
    return this._fixedPageSize;
  }

  /**
   * The filters that will be displayed with checkboxes above the table.
   */
  filters = input<ITableControlFilter[]>([]);
  /**
   * The actions that will be displayed as buttons above the table.
   */
  actions: ModelSignal<ITableAction[]> = model<ITableAction[]>(DEFAULT_TABLE_ACTIONS);
  pdf = signal<IPdfBlob>(null);
  pdfSrc = computed(() => {
    const pdf = this.pdf();
    const url = pdf?.blob ? this.sanitizer.bypassSecurityTrustResourceUrl(URL.createObjectURL(pdf.blob)) : null
    console.log('pdfSrc', url);
    return url;
  });

  @Input() dataSource: IDatasource = {
    rowCount: null,
    getRows: (params: IGetRowsParams) => {
      patchState(this.state, _ => ({loading: true}));
      // increment request counter. If the counter is zero, it is the first request
      this.openGetRowReqs$.next(
        this.openGetRowReqs$.getValue() == null
          ? 1
          : this.openGetRowReqs$.getValue() + 1
      );
      combineLatest([
        this.apiInstance$.pipe(filter(v => {
          return v != null
        })),
        this.currentViewPort$,
        this.gridApiInitialized$
      ]).pipe(
        take(1),
        tap((): void => {
          console.log('getRows', params);
          const endRow: number =
            params.endRow < this.rowCount ? params.endRow : this.rowCount;
          const startRow: number = params.startRow >= params.endRow ?
            Math.max(0, params.endRow - this.gridOptions.cacheBlockSize) : params.startRow;
          const newViewPort = new ViewPort(
            startRow,
            endRow,
            this.lazyLoadColumns === false ? 0 : this.currentViewPort$.getValue().startCol,
            this.lazyLoadColumns === false && this.apiCols ? this.apiCols.length - 1 :
              this.currentViewPort$.getValue().endCol,
          );
          this.currentViewPort$.next(newViewPort);
        }),
        mergeMap(() => {
          return this.fetchViewport();
        }),
        // Placed after the http request so that a cancel really unsubscribes (and thereby
        // aborts) an in-flight fetch instead of only chains still waiting for the instance.
        takeUntil(
          this.cancel$.pipe(
            filter((v: boolean): boolean => v === true),
            tap((_: boolean): void => {
              this.openGetRowReqs$.next(this.openGetRowReqs$.getValue() - 1);
              patchState(this.state, _ => ({loading: false}));
              params.failCallback();
            })
          )
        )
      ).subscribe(
        {
          next: (rowBlock: ICustomGridRow[] | null): void => {
            if (rowBlock == null) {
              // Stale response of an outdated cache generation — discard.
              this.openGetRowReqs$.next(this.openGetRowReqs$.getValue() - 1);
              patchState(this.state, _ => ({loading: false}));
              params.failCallback();
              return;
            }
            try {
              console.log(rowBlock);
              params.successCallback(rowBlock, this.rowCount);
              this.cdr.detectChanges();
              // decrement the counter for the open requests
              this.openGetRowReqs$.next(this.openGetRowReqs$?.getValue() - 1);
              // Select all rows
              if (this.selectedAll) {
                rowBlock.forEach((row: ICustomGridRow) => {
                  const gridRow = this.gridApi.getRowNode(row.apiRow.id);
                  if (gridRow) {
                    this.setInitialSelection(gridRow);
                  }
                });
              } else {
                // reset the previous selection
                console.log(this.savedSelectedRowsIds);
                this.savedSelectedRowsIds.forEach((id) => {
                  const gridRow = this.gridApi.getRowNode(id);
                  if (gridRow) {
                    this.setInitialSelection(gridRow);
                  }
                });

                // Select rows if they are in the selection
                const updatedSelections = this.selectedRows().map(row => {
                  const gridRow = this.gridApi.getRowNode(row.apiRow.id);
                  // if gridRow is undefined
                  if (gridRow) {
                    if (!gridRow.isSelected()) {
                      this.setInitialSelection(gridRow);
                    }
                    return gridRow.data as ICustomGridRow;
                  }
                  return row;
                });

                this.selectedRows.set(updatedSelections);
              }

              const eventData: IRowDataChange = {selected: this.selectedRows(), all: rowBlock};
              this.rowDataChanged.emit(eventData);
              this.rowDataChanged$.next(eventData);
              this.fitColumns();
              patchState(this.state, _ => ({loading: false}));

            } catch (e) {
              console.error("Error while handling the getRows response", e);
              // decrement the counter
              this.openGetRowReqs$.next(this.openGetRowReqs$.getValue() - 1);
              patchState(this.state, _ => ({loading: false}));
              this.gridApi.hideOverlay();
              return params.failCallback();
            }
          },
          error: (err) => {
            console.error("Error while fetching rows", err);
            // decrement the counter
            this.openGetRowReqs$.next(this.openGetRowReqs$.getValue() - 1);
            patchState(this.state, _ => ({loading: false}));
            this.gridApi.hideOverlay();
            return params.failCallback();
          }
        })
    },
  };

  /**
   * Event for custom table actions. If the actions input above differ from the DEFAULT_TABLE_ACTIONS and you need
   * to react in the parent component of this one, you can listen to the custom action output.
   */
  @Output() customAction = new EventEmitter<EControlActions>();

  /**
   * Reference to the parent component.
   */
  @Input() parentRef: any;
  /**
   * Indices of columns that are static.
   */
  @Input() staticColumns: number[] = [];

  @Input() lazyLoadColumns = false;

  /**
   * Event that fires when the ag grid is ready. The api for the ag grid is ready when the event fires.
   */
  @Output() gridReady = new EventEmitter<IGridReady>();
  /**
   * One Row of the table is deleted, the api request is finished successfully when it triggers.
   */
  @Output() rowDeleted = new EventEmitter<RowNode>();

  /**
   * A new row was created. The api request is finished successfully when it triggers.
   */
  @Output() rowCreated = new EventEmitter<IApiRow>();
  /**
   * Column index that will get edited after a new line was created.
   */
  @Input() editAfterCreateIndex = 0;

  /**
   * Event that triggers when a cell value has changed.
   */
  @Output() cellValueChanged = new EventEmitter<CellValueChangedEvent>();
  /**
   * Emits when a form cell editor gets confirmed.
   * Is needed in the easy plan table to check if the date is in range of the current date range filter.
   */
  @Output() formCellEditorConfirmed = new EventEmitter<{
    result: boolean;
    date: NgbDate;
  }>();

  @Output() paginationChanged$ = new EventEmitter();
  @Output() rowSelected = new EventEmitter<RowNode[]>();
  @Output() rowDeselected = new EventEmitter<RowNode[]>();
  @Output() savedRowSelected = new EventEmitter<RowNode[]>();
  @Output() cellDoubleClicked = new EventEmitter<CellDoubleClickedEvent>();
  @Output() cellClicked = new EventEmitter<CellClickedEvent>();
  @Output() currentViewPortChanged = new EventEmitter<ViewPort>();
  @Output() rowDataChanged: EventEmitter<IRowDataChange> = new EventEmitter<IRowDataChange>();
  @Output() firstDataRendered = new EventEmitter<FirstDataRenderedEvent>();

  @Output() rowsAdded = new EventEmitter<RowEvent>();

  scrollThrottleTimeout: ReturnType<typeof setTimeout>; // Variable to store the throttle timeout

  // Client side table model. It stores all fetched cells from the api. It is then used to merge data that already
  // lies on the client side, with new data loaded when getRows is triggered with a new viewport.
  viewPortTable = new ViewPortTable();

  context: TableControlComponent;

  private _rowCount: number;
  // The saved rows will get selected again after getting data from getRows.
  protected savedSelectedRowsIds: string[] = [];
  // client column filter / sort has changed
  private columFilterOrSortChange$ = new EventEmitter();
  // Helper Observable to cancel open getRows. Should be fired when the instance has changed or the filter / sorting
  private cancel$ = new BehaviorSubject(false);
  // Observable for open getRow Requests
  private gridApiInitialized$ = new BehaviorSubject(false);

  private openGetRowReqs$ = new BehaviorSubject<number>(null);

  private debouncedVirtualColumnChange$ = new EventEmitter<VirtualColumnsChangedEvent>();
  private currentViewPort$ = new BehaviorSubject<ViewPort>(
    new ViewPort(0, 0, 0, 0)
  );

  // The debounce time in ms for the virtual column change event.
  // Used to prevent overloading of api calls for loading cells of the table.
  private virtualColumnLoadingTime = 300;

  private selectedAll = false;

  private gridContainerHeight$ = new BehaviorSubject<number>(0);
  private automaticallySetRowSelection = new Set();

  protected rowDataChanged$: BehaviorSubject<IRowDataChange> = new BehaviorSubject<IRowDataChange>(null);
  protected rowChanged$: BehaviorSubject<void> = new BehaviorSubject<void>(null);

  rowCountSignal: WritableSignal<number> = signal(null);

  // saves the selected rows before changing and fetching data.
  get rowCount(): number {
    return this._rowCount;
  }

  protected set rowCount(n: number) {
    if (this.gridApi) {
      this.gridApi.setRowCount(n);
    }
    this._rowCount = n;
    this.rowCountSignal.set(n);
  }

  override toModel(d: IApiTableData) {
    this.rowCount = d.rowcount;
    const tempData = this.apiAdapter.from(d);

    this.columnDefs = tempData.columnDefs;
    this.apiCols = tempData.displayedColumns;
    if (this.dataSource && this.gridApi) {
      this.initializeDataSource(this.gridApi, this.dataSource);
    }

    this.cdr.detectChanges();
    this.fitColumns();
    this.gridApi?.refreshHeader();
  }

  constructor() {
    super();
    this.context = this;
    effect(() => {
      const pdf = this.pdf();
      if (pdf) {
        queueMicrotask(() => {
          untracked(() => this.showPdfOverlay(pdf));
        });
      } else {
        queueMicrotask(() => {
          untracked(() => this.hidePdfOverlay());
        });
      }
    });
    this._toolbarService.resetFullTextFilter$.pipe(
      takeUntilDestroyed()
    ).subscribe(() => {
      this.gridApi?.setFilterModel(null);
    })

    this._rowSelection.changed.pipe(
      takeUntilDestroyed(),
      tap((event: SelectionChange<string>): void => {
        if (!event) return;

        this.gridApi?.deselectAll();

        this._updateSelectedRowsOnSelectionChange(event);

        if (event.removed.length > 0) {
          const deselected = event.removed.map(r => this.gridApi?.getRowNode(r));
          this.rowDeselected.emit(deselected);
        }

        if (event.added.length > 0) {
          const savedSelectedRows: RowNode[] = [];
          event.added.forEach(r => {
            if (this.automaticallySetRowSelection.has(r)) {
              this.automaticallySetRowSelection.delete(r);
              savedSelectedRows.push(this.gridApi?.getRowNode(r));
            }
          });
          if (savedSelectedRows.length > 0) {
            this.savedRowSelected.emit(savedSelectedRows);
          }
          const selected = event.added.map(r => this.gridApi?.getRowNode(r));
          this.rowSelected.emit(selected);
        }


      })
    ).subscribe()

  }

  private _updateSelectedRowsOnSelectionChange: (event: SelectionChange<string>) => void
    = (event: SelectionChange<string>): void => {
    this.selectedRows.set(event.source.selected.reduce((acc: ICustomGridRow[], id: string): ICustomGridRow[] => {
      const gridRow: RowNode = this.gridApi?.getRowNode(id);
      const row: ICustomGridRow
        = (gridRow?.data as ICustomGridRow) || (this.viewPortTable.getRowById(id) as ICustomGridRow);
      if (row) {
        if (gridRow && !gridRow.isSelected()) {
          gridRow.setSelected(true);
        }
        acc.push(row);
      }
      return acc;
    }, []))
  }


  ngOnInit(): void {
    this._tabActivatedDirective.registerTable(this)

    // Add onCellValueChanged callback to gridOptions
    this.gridOptions.onCellValueChanged = (event: CellValueChangedEvent): void => {
      this.onCellValueChanged(event);
    };
    this.subs.add(
      this.debouncedVirtualColumnChange$
        .pipe(
          filter(() => this.lazyLoadColumns === true),
          debounceTime(this.virtualColumnLoadingTime),
          filter(
            (virtualColumnChange) =>
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore
              (virtualColumnChange.columnApi).columnModel != null
          )
        )
        .subscribe((virtualColumnChange) => {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          const colFields: { field: string; key: string }[] = (virtualColumnChange.columnApi).columnModel
            .viewportColumns.map((c: Column) => {
              return {
                field: (c.getColDef() as ICustomColDef).apiCol.field,
              };
            });
          if (colFields.length <= 0) {
            return;
          }
          console.log('debounced col change', colFields, this.staticColumns);

          const newViewPort = new ViewPort(
            this.currentViewPort$.getValue().startRow,
            this.currentViewPort$.getValue().endRow,
            Number(colFields[0].field),
            colFields.length == this.staticColumns.length
              ? Number(colFields[colFields.length - 1].field)
              : Number(
                colFields[colFields.length - 1 - this.staticColumns.length]
                  .field
              )
          );
          if (newViewPort.equals(this.currentViewPort$.getValue())) {
            return;
          }
          this.currentViewPort$.next(newViewPort);
          const vp = this.viewPortTable.viewPortChanged(
            this.currentViewPort$.getValue(),
            'col'
          );
          if (vp) {
            console.log('viewport changed. refreshing data');
            // Trigger the function get rows() to get the new viewport from the server
            this.refreshInfiniteCache();
          }
        })
    );

    // debounced col filter
    this.subs.add(
      this.columFilterOrSortChange$
        .pipe(
          tap(_ => {
            this.cancel$.next(true)
          }),
          debounce(() => timer(500)),
          tap(() => this.changeFilterAndSorting()),
          filter(errored => {
            if (errored == null) {
              this.cancel$.next(false)
              return false
            }
            patchState(this.state, _ => ({loading: false}));
            return true
          })
        )
        .subscribe((value) => {
          this.refreshInfiniteCache();
          console.log(value);
        })
    );

    // Listen to all get row calls and when they are finished, hide the loading overlay.
    this.subs.add(
      this.openGetRowReqs$
        .pipe(
          filter((n: number) => {
            return n === 0;
          })
        )
        .subscribe(() => {
          if (this.rowCount <= 0) {
            this.gridApi.showNoRowsOverlay();
          } else {
            this.gridApi.hideOverlay();
            this.rowsAdded.emit();
          }
        })
    );

    // If the height of the grid container changes, re-calculate the pagination size
    this.subs.add(
      this.gridContainerHeight$.pipe(
        filter(v => v != 0),
        debounceTime(30),
        distinctUntilChanged(),
        tap((v) => {
          console.log('Grid container height changed. Resetting the pagination page size. Height is: ', v);
          this.setPaginationPageSize();
        })
      ).subscribe()
    )
  }

  ngAfterViewInit() {

  }

  override ngOnDestroy() {
    this._tabActivatedDirective.unregisterTable(this);
    super.ngOnDestroy();
    this.resetRowCache();
  }

  changeFilterAndSorting(): void {
    // build column filter
    let columnFilter: IColumnFilter[] = [];
    let apiSortModel: IApiSortModel = {sortby: [], direction: []}
    if (!(this.gridApi == null || this.apiInstance() == null)) {
      const filterModel: { [p: string]: any } = this.gridApi.getFilterModel();
      columnFilter = this.getApiFilterModel(filterModel);

      // build column sort
      const sortModel: IReducedColumnState[] = this.colApi.getColumnState().map((c) => {
        return {colId: c.colId, sort: c.sort, sortIndex: c.sortIndex};
      });
      apiSortModel = this.getApiSortModel(sortModel);

      console.log('filterModel', filterModel);
      console.log('sortModel', apiSortModel);
    }

    const newFilter: IFilter = {...this.filter(), columnfilter: columnFilter};
    const newSorting: object = apiSortModel || this.sorting();
    // No-op pulses (e.g. sortChanged fired by a resize without an actual change) must not
    // patch state: they would set loading without a fetch ever resolving it.
    if (JSON.stringify(newFilter) === JSON.stringify(this.filter())
      && JSON.stringify(newSorting) === JSON.stringify(this.sorting())) {
      return;
    }

    patchState(this.state, _ => ({
      loading: true,
      filter: newFilter,
      sorting: newSorting,
    }));
  }

  /**
   * Saves a copy of the grid api for latter usability.
   * @param params
   */
  onGridReady(params: IGridReady) {
    this.gridApi = params.api;
    this.gridApiSig.set(params.api);
    this.colApi = params.columnApi;
    this.gridReady.emit(params);
    this.fitColumns();
    this.setInitialCacheBlockSize();
    this.setPaginationPageSize();
    this.gridApiInitialized$.next(true);
    if (this.dataSource && this.gridApi) {
      this.initializeDataSource(this.gridApi, this.dataSource);
    }
  }

  setInitialCacheBlockSize(): void {
    // Force new cache block size by resetting internal cache
    document.querySelector('.ag-paging-panel')
    const gridHeight: number
      = this.gridElementRefs.get(0).nativeElement.querySelector('.ag-body-viewport')?.offsetHeight || null;

    if (!gridHeight) {
      console.warn('No grid height could be calculated. ' +
        'This is probably due to some elements not being rendered yet.');
      return;
    }

    const rowHeight: number = this.gridOptions.rowHeight;
    const numberOfRowsInViewPort: number = (Math.floor(gridHeight / rowHeight) + 1);
    // If the fixed page Size is set, fetch double the number of rows for smoother scrolling UX.
    const cacheBlockSize: number = Math.max(1,
      this.fixedPageSize ? Math.min(numberOfRowsInViewPort * 2, this.fixedPageSize) : numberOfRowsInViewPort + 11);
    if (cacheBlockSize != this.gridOptions.cacheBlockSize) {
      (this.gridOptions.api as any).gridOptionsWrapper.setProperty('cacheBlockSize', cacheBlockSize);
    }
  }

  setPaginationPageSize() {
    if (!this.gridApi) return;
    if (this.gridOptions.pagination !== true) return;
    if (!this.gridElementRefs?.get(0)?.nativeElement) return;
    const hasChanged: boolean = this.resetPaginationPageSize();
    if (hasChanged) {
      // Refresh the cache directly instead of abusing onSortChanged(), which would also run
      // the (sortChanged) filter/sort pipeline although neither filter nor sorting changed.
      this.gridApi.purgeInfiniteCache();
    }
  }

  resetPaginationPageSize(): boolean {
    let hasChanged: boolean = false;
    // Force new cache block size by resetting internal cache
    document.querySelector('.ag-paging-panel')
    const gridHeight: number =
      this.gridElementRefs.get(0).nativeElement.querySelector('.ag-paging-panel')?.getBoundingClientRect().top -
      this.gridElementRefs.get(0).nativeElement.querySelector('.ag-header-viewport')?.getBoundingClientRect().bottom
      || this.gridElementRefs.get(0).nativeElement.querySelector('.ag-body-viewport')?.offsetHeight
      || null

    if (!gridHeight) {
      console.warn('No grid height could be calculated. ' +
        'This is probably due to some elements not being rendered yet.');
      return false;
    }

    const rowHeight: number = this.gridOptions.rowHeight;
    const numberOfRowsInViewPort: number = (Math.floor(gridHeight / rowHeight) + 1);
    // If the fixed page Size is set, fetch double the number of rows for smoother scrolling UX.
    const cacheBlockSize: number = Math.max(1,
      this.fixedPageSize ? Math.min(numberOfRowsInViewPort * 2, this.fixedPageSize) : numberOfRowsInViewPort + 11);
    if (cacheBlockSize < this.gridOptions.cacheBlockSize) {
      (this.gridOptions.api as any).gridOptionsWrapper.setProperty('cacheBlockSize', cacheBlockSize);
    }
    const pageSize: number = (this.pdf() ? 1 : this.fixedPageSize) || cacheBlockSize;
    // a threshold of 2 prevents high frequent fetch rows triggered by small changes in height such as scroll bars
    hasChanged = Math.abs(cacheBlockSize - this.gridOptions.cacheBlockSize) > 2 ||
      pageSize !== this.gridOptions.paginationPageSize;
    (this.gridOptions.api as any).gridOptionsWrapper.setProperty('cacheBlockSize', cacheBlockSize);
    (this.gridOptions.api as any).gridOptionsWrapper.setProperty('paginationPageSize', pageSize);
    if (hasChanged) {
      // Only abort in-flight row fetches when the page size actually changed — resize pulses
      // without a size change must not cancel running requests.
      this.cancel$.next(true);
      this.cancel$.next(false);
    }
    return hasChanged;
  }


  /**
   * Stores the selected cells value in the clipboard.
   */
  @Input() onCopyValue() {
    // If the user has selected some string, do the browser's native behavior
    if (window.getSelection().toString() != '') return;

    const focusedCell = this.gridApi.getFocusedCell();
    if (!focusedCell) return;

    const rowNode = this.gridApi.getDisplayedRowAtIndex(focusedCell.rowIndex);
    const cellValue = this.gridApi.getValue(focusedCell.column, rowNode);
    if (cellValue == null) {
      this.clipboard.copy(null);
      this.clipboardService.clipboardValue = JSON.stringify('');
    } else if (typeof cellValue.value === 'object') {
      this.clipboard.copy(JSON.stringify(cellValue.value));
      this.clipboardService.clipboardValue = JSON.stringify(cellValue.value);
    } else {
      this.clipboard.copy(cellValue.value);
      this.clipboardService.clipboardValue = cellValue.value;
    }
    console.log('Copied to clipboard!', this.clipboardService.clipboardValue);
    this.feedbackService.setNotification(
      'Der Wert wurde in die Zwischenablage kopiert.'
    );
  }

  onPaste(e: ClipboardEvent) {
    console.log('on paste', e)
    // Stop data actually being pasted into the element
    e.stopPropagation();
    e.preventDefault();

    // Get pasted data via clipboard API
    const clipboardData = e.clipboardData;
    const pastedData = clipboardData.getData('Text');
    console.log('Pasted data', pastedData)
    this.onPasteValue(pastedData);
  }

  /**
   * Pastes the stored clipboard value onto the focused cell.
   */
  @Input() onPasteValue(pastedValue: string) {
    if (!pastedValue && pastedValue !== '') throw Error('Could not paste value. Pasted value is undefined.');

    const focusedCell = this.gridApi.getFocusedCell();
    if (!focusedCell) {
      console.warn('Could not paste value, because no focused cell could be found.');
      return;
    }
    const rowNode = this.gridApi.getDisplayedRowAtIndex(focusedCell.rowIndex);

    if (!rowNode) {
      console.warn('Could not paste value, because no focused cell could be found.');
      return;
    }
    // // Check if it is an object that got pasted
    // let pasteObject: IPastedObject;
    // try {
    //   let parsedClipboardValue = JSON.parse(pastedValue);
    //   pasteObject = {
    //     clipboardValue: parsedClipboardValue,
    //     column: focusedCell.column.getColId(),
    //   };
    // } catch (e) {
    //   pasteObject = {
    //     clipboardValue: pastedValue,
    //     column: focusedCell.column.getColId(),
    //   };
    // }

    this.gridApi.startEditingCell({
      rowIndex: rowNode.rowIndex,
      colKey: focusedCell.column.getColId(),
      charPress: `#pasted${JSON.stringify(pastedValue)}`,
    });
  }

  /**
   * Applies the column filter by searching for and programmatically clicking the apply-button within the provided
   * filter element.
   *
   * @param filterElement - The DOM element containing the filter interface
   */
  private applyColumnFilter(filterElement: Element): void {
    // Search for an apply-button in open filters
    const filterButtonElement: Element = filterElement.querySelector('.ag-filter-apply-panel-button');

    if (filterButtonElement) {
      // Cast to HTMLElement and simulate click
      (filterButtonElement as HTMLElement).click();
    }
  }

  onCellValueChanged(evt: CellValueChangedEvent): void {
    this.cellValueChanged.emit(evt);
    this.rowChanged$.next();
  }

  /**
   * Duplicates a line of a table. Calls the api method and fires events afterward.
   */
  duplicateLine(): Observable<any> {
    const selectedRows = this.selectedRows();
    // get array of objects for mass action
    const objects = selectedRows.map((r) => r.apiRow.obj);
    // find the highest RowIdx to pass to the api function call. The duplications will be inserted at the last row idx then.
    const highestRowIdx = selectedRows.reduce((previousValue, currentValue) => {
      if (previousValue < currentValue.apiRow.rowidx) {
        return currentValue.apiRow.rowidx;
      } else {
        return previousValue;
      }
    }, -1);
    // api call
    return this._tableControlService.tableControlDuplicateLine({
      InstanceId: this.apiInstance().instanceid,
      RowObjects: objects,
      RowIdx: highestRowIdx
    }).pipe(
      tap(() => {
        this.rowCount = this.rowCount + selectedRows.length;
        // listen to the row create event once to redraw rows after creating / deleting a row
        this.DimensionChanged().subscribe();
        // re-fetch rows
        this.refreshInfiniteCache();

        this.rowCreated.emit();
      }),
      catchError((err) => {
        this.gridApi.hideOverlay();
        return of(err);
      })
    );
  }

  addRowsAndRefetch(rowsAdded: number): void {
    this.rowCount = this.rowCount + rowsAdded;
    // listen to the row create event once to redraw rows after creating / deleting a row
    this.DimensionChanged().subscribe();
    // re-fetch rows
    this.refreshInfiniteCache();

    this.rowCreated.emit();
  }

  /**
   * Creates a new empty row in the table by calling the api. After receiving
   * a new api-RowInterface the row will be mapped to an ag-grid row and then inserted at the
   * start of the grid row array.
   */
  newLine(editAfterCreate = true, newLineParams: INewLineParams = this.newLineParams): Observable<any> {
    let newLineObs;

    // if the new line needs a name, open a dialog to get the name
    if (newLineParams) {
      newLineObs = this.dialog.open(
        NewEntryDialogComponent,
        {data: {edit: false, categoryLabel: "Name der Wache"}},
      ).afterClosed()
        .pipe(
          switchMap((newName) => {
            return this._tableControlService.tableControlNewLine({
              InstanceId: this.apiInstance().instanceid, Data: {[newLineParams.field]: newName}
            })
          })
        )
    } else {
      newLineObs = this._tableControlService.tableControlNewLine({
        InstanceId: this.apiInstance().instanceid
      })
    }

    return newLineObs.pipe(
      tap((row: IApiRow) => {
        this.rowCount++;
        // listen to the row create event once to redraw rows after creating / deleting a row
        // this.DimensionChanged();
        this.DimensionChanged()
          .pipe(
            filter(() => {
              return this.editAfterCreateIndex != null;
            })
          )
          .subscribe(() => {
            if (editAfterCreate) {
              this.gridApi.startEditingCell({
                rowIndex: row.rowidx,
                colKey: Object.keys(row.cols)[this.editAfterCreateIndex],
              })
            }
          });
        this.refreshInfiniteCache();
        this.rowCreated.emit(row)
      }),
      catchError((err) => {
        this.gridApi.hideOverlay();
        return of(err);
      })
    )
  }

  /**
   * Opens a dialog to edit the selected line as a form. When closed, it calls the changeLine api method and fires events
   * for notifications of other components.
   */
  editRow(): Observable<any> {
    const selectedRow: ICustomGridRow = this.selectedRows()[0];
    if (!selectedRow) {
      return EMPTY;
    }

    return this.dialog.open(EditRowDialogComponent, {
      data: {
        parentGUID: this.GUID,
        row: selectedRow,
        columns: this.apiData().displayedColumns,
        apiInstance: this.apiInstance(),
        groupMap: this.groupMap,
      }
    }).afterClosed().pipe(
      tap(() => {
        this.refreshInfiniteCache();
      }),
    );
  }

  /**
   * Deletes x entries of the table. Calls the api method and fires events for notifications to other components.
   */
  deleteLine(specialCall?: Observable<boolean>): Observable<any> {
    const selectedRows = this.selectedRows();
    const rowObjects = selectedRows
      .sort((a, b) => a.apiRow.rowidx - b.apiRow.rowidx)
      .map((r) => r.apiRow.obj);
    return (specialCall || this._tableControlService.tableControlDeleteLine({
      InstanceId: this.apiInstance().instanceid,
      RowObjects: rowObjects
    })).pipe(
      tap((res) => this.afterDelete(res, selectedRows)),
      catchError((err) => {
        this.gridApi.hideOverlay();
        return of(err);
      })
    );
  }

  private afterDelete(res: boolean, selectedRows: any[]) {
    const errorRooms: boolean[] = [];

    // successful
    if (res == true) {
      // decrement row count
      this.rowCount = this.rowCount - selectedRows.length;
      // listen to the row create event once to redraw rows after creating / deleting a row
      this.DimensionChanged().subscribe();
      this.rowDeleted.emit();
      // deselect the rows because they got deleted
      this.gridApi.deselectAll();
      this.resetSelection();
      this.refreshInfiniteCache();
    } else if (res == false) {
      errorRooms.push(res);
    }
    // show an error if the response is false
    if (errorRooms.length > 0) {
      this.feedbackService.setError(
        `Die Löschung konnte nicht durchgeführt werden. Ein Objekt ist noch in Benutzung.`
      );
      this.gridApi.hideOverlay();
    }

  }

  deleteSingleLine(apiRow: IApiRow) {
    this._tableControlService.tableControlDeleteLine({
      InstanceId: this.apiInstance().instanceid,
      RowObjects: [apiRow.obj]
    }).subscribe(
      {
        next: (res) => {
          // successful
          if (res == true) {
            // decrement row count
            this.rowCount = this.rowCount - 1;
            // listen to the row create event once to redraw rows after creating / deleting a row
            this.DimensionChanged().subscribe();
            this.rowDeleted.emit();
            // deselect the rows because they got deleted
            this.gridApi.deselectAll();
            this.resetSelection();
            this.refreshInfiniteCache();
          }
        },
        error: () => {
          this.gridApi.hideOverlay();
        }
      }
    );
  }

  /**
   * Calls a function of this component by the enum of the function
   * @param controlAction Enum of the action that was triggered
   * @param isDisabled Denotes that the action is disabled to cancel the execution
   */
  callActionFunction(controlAction: EControlActions, isDisabled: boolean = false): void {
    if (isDisabled) return;
    if (!this.instanceId()) return;
    this.gridApi.showLoadingOverlay();
    let actionRequest: Observable<any>;
    switch (controlAction) {
      case EControlActions.create:
        actionRequest = this.newLine();
        break;
      case EControlActions.copy:
        actionRequest = this.duplicateLine();
        break;
      case EControlActions.editRow:
        actionRequest = this.editRow();
        break;
      case EControlActions.delete:
        actionRequest = this.deleteLine();
        break;
      case EControlActions.selectAll:
        this.selectedAll = true;
        this.refreshInfiniteCache();
        break;
      case EControlActions.csvExport:
        actionRequest = this.downloadCsv();
        break;
      case EControlActions.csvImport:
        actionRequest = of(null).pipe(tap(() => this.fileUploadRef.nativeElement.click()))
        break;
      default: {
        this.customAction.emit(controlAction);
        const tableAction = this.actions().find(a => a.controlAction === controlAction);
        if (!tableAction?.action) {
          this.gridApi?.hideOverlay();
          return;
        }
        actionRequest = tableAction.action();
        break;
      }
    }
    if (controlAction !== EControlActions.selectAll) {
      actionRequest.pipe(
        finalize(() => this.gridApi.hideOverlay())
      ).subscribe(() => {
        this.controlStore.controlDataChanged$.emit({changeType: controlAction, GUID: this.GUID});
      });
    }
  }

  // Changes the control instance and refreshes the view.
  refresh(): void {
    let filterModel;
    let columnFilter: IColumnFilter[];
    let sortModel;
    let apiSortModel: IApiSortModel;

    // show the loading overlay
    if (this.gridApi) {
      this.gridApi.showLoadingOverlay();
      // Save the selected rows before refreshing the data rows.
      this.savedSelectedRowsIds = this.selectedRows().map((r) => r.apiRow.id);

      filterModel = this.gridApi.getFilterModel();
      columnFilter = this.getApiFilterModel(filterModel);

      // build column sort
      sortModel = this.colApi.getColumnState().map((c) => {
        return {colId: c.colId, sort: c.sort, sortIndex: c.sortIndex};
      });
      apiSortModel = this.getApiSortModel(sortModel);
    }
    // Cancel ongoing getRows requests.
    this.cancel$.next(true);
    this.cancel$.next(false);
    this.resetRowCache();
    this.resetSelection();
    this.selectedAll = false;

    patchState(this.state, state => ({
      filter: {
        ...state.filter,
        columnfilter: columnFilter
      },
      sorting: apiSortModel
    }))

    console.log('filterModel', filterModel);
    console.log('sortModel', apiSortModel);
  }

  /**
   * Automatically resizes all columns based on their content.
   *
   * Columns that were previously wider than the maximum allowed width (`max`) will retain their current width and are
   * not resized.
   * All other columns will be auto-sized but capped at the provided `max` width.
   *
   * @param max - Optional maximum width (in pixels) for auto-sized columns. Defaults to `this.maxAutoColWidth`.
   */
  fitColumns(max: number = this.maxAutoColWidth): void {
    if (this.gridApi && this.colApi.getColumnState() != null) {
      const columns: Column[] = this.colApi.getColumns().filter((col) => !this.manualResizedColumns.has(col.getColId()));
      const columnIds: string[] = [];

      const previousWidths = new Map<string, number>();
      columns.forEach((column: Column): void => {
        previousWidths.set(column.getColId(), column.getActualWidth());
        columnIds.push(column.getColId());
      });

      this.colApi.autoSizeColumns(columnIds, false);

      columns.forEach((col: Column): void => {
        const colId: string = col.getColId();
        const newWidth: number = col.getActualWidth();
        const oldWidth: number = previousWidths.get(colId) ?? 0;

        if (newWidth > max && oldWidth <= max) {
          this.colApi.setColumnWidth(col, max);
        } else if (oldWidth > max) {
          this.colApi.setColumnWidth(col, oldWidth);
        }
      });
      console.log('visible columns resized');
    }
  }

  onColumnResize(evt: ColumnResizedEvent): void {
    if (evt.source === "uiColumnDragged") {
      this.manualResizedColumns.add(evt.column.getColId());
    }
  }

  getRowNodeId: GetRowIdFunc<ICustomGridRow> = (params: { data: ICustomGridRow }) => {
    return params.data?.apiRow?.id;
  };

  @Input() onCellClicked(evt: CellMouseDownEvent) {
    console.log(evt);
    this.gridApi?.setFocusedCell(evt.rowIndex, evt.column);
    this.cellClicked.emit(evt);
  }

  @Input() onCellContextMenu($event: CellContextMenuEvent) {
  }

  // A filter for the instance changed. (Not column filter)
  onFilterChange(evt: boolean, filter: ITableControlFilter) {
    filter.active = evt;
    const mappedFilters = this.toFilterDto(this.filters());

    const newFilter = {
      ...this.apiInstance().filter,
      ...mappedFilters,
    };
    // check if changed
    if (
      !sameArrayValues(
        Object.keys(this.apiInstance().filter),
        Object.keys(newFilter)
      ) ||
      !sameArrayValues(
        Object.values(this.apiInstance().filter),
        Object.values(newFilter)
      )
    ) {
      this.patchFilter({
        ...mappedFilters,
      })
    }
  }

  toFilterDto(filter: ITableControlFilter[]): Record<string, boolean> {
    return filter.reduce((acc, f) => {
      acc[f.apiAttributeKey] = f.active;
      return acc
    }, {} as Record<string, boolean>)
  }

  // Delayed column filter trigger
  onColumnFilterClose(): void {
    this.columFilterOrSortChange$.emit();
  }

  // Delayed column sort trigger
  onSortChange() {
    this.columFilterOrSortChange$.emit();
  }

  // Trigger the debounce column change event
  onVirtualColumnChange(evt: VirtualColumnsChangedEvent) {
    this.debouncedVirtualColumnChange$.emit(evt);
  }

  // Maps the internal ag grid filter model to server filter model.
  private getApiFilterModel(filterModel: { [p: string]: any }): IColumnFilter[] {
    if (!filterModel) return null;
    const columnfilter: IColumnFilter[] = [];
    Object.keys(filterModel).forEach((fm) => {
      // The filter model is keyed by the column position, so a filter can outlive the column it was
      // set on whenever the table changes its column set.
      const column = this.colApi.getColumn(String(fm));
      if (!column) {
        console.warn(`Dropping column filter of removed column "${fm}".`);
        return;
      }

      const colDef = column.getColDef() as ICustomColDef;
      const apiCol = colDef.apiCol;
      if (!apiCol) {
        console.warn(`Dropping column filter of column "${fm}" without api column.`);
        return;
      }

      // A filter that does not match the column's filter would make the backend read the column
      // value as the wrong type and fail the whole request.
      const expression = filterModel[String(fm)];
      if (!isFilterExpressionCompatible(colDef.filter, expression)) {
        console.warn(
          `Dropping "${expression?.filterType}" filter on column "${apiCol.attribute}": incompatible with the column filter "${colDef.filter}".`
        );
        return;
      }

      const obj = {
        attribute: apiCol.attribute,
        attributeindex: apiCol.attributeindex,
        expression,
      };
      columnfilter.push(obj);
    });
    return columnfilter;
  }

  resetRowCache() {
    console.log('resetting row cache');

    // Invalidate in-flight fetches: their responses were computed against the old cache
    // state and must not be written into the fresh one.
    this.fetchGeneration++;
    this.viewPortTable.resetCache();
  }

  // Gets the current viewport from the server, adds the row block to the client model and returns the full

  // Maps the ag grid sorting model to the server sort model.
  private getApiSortModel(sortModel: IReducedColumnState[]): IApiSortModel {
    if (sortModel.length == null) {
      return {};
    }
    const sortby: string[] = [];
    const direction: (0 | 1)[] = [];
    sortModel.filter(sm => sm.sort != null).sort((a, b) => a.sortIndex - b.sortIndex).forEach((sm) => {
      sortby.push((
        (this.colApi.getColumn(sm.colId).getColDef() as ICustomColDef)
          .attribute +
        ';' +
        (this.colApi.getColumn(sm.colId).getColDef() as ICustomColDef)
          .attributeindex
      ));
      direction.push(sm.sort == 'asc' ? 0 : 1);
    });
    return {
      sortby,
      direction,
    };
  }

  refreshRepositories() {
    const colDefs = this.gridApi.getColumnDefs() as ICustomColDef[];
    // api requests for all columns that have a repository
    const apiReqs = colDefs
      // only for columns with repo
      //   .filter((column: ICustomColDef) => {
      //   return column.repository != null;
      // })
      // map column to api request
      .map((column: ICustomColDef) => {
        if (column.repository != null) {
          return this._tableService.tablePostGetRepositoryForProperty({
            _parameters: [
              this.apiInstance().instanceid,
              column.attribute,
              column.attributeindex
            ]
          });
        } else {
          return of(column);
        }
      });
    // execute all api requests
    forkJoin(apiReqs).subscribe((res: any[]) => {
      res.forEach((repo, index) => {
        if (Array.isArray(repo)) {
          colDefs[index].repository = repo;
        }
      });
      this.gridApi.setColumnDefs(colDefs);
      const editedCell = this.gridApi.getEditingCells()[0];
      // If there was a cell in edit and the editor was the reference selection editor => stop editing and start again to update the repo.
      if (editedCell) {
        // this.gridApi.stopEditing();
        this.gridApi.startEditingCell({
          rowIndex: editedCell.rowIndex,
          colKey: editedCell.column.getColId(),
        });
      }
      console.log(colDefs);
      console.log(res);
    });
  }

  // Incremented on every cache reset; responses started under an older generation are stale.
  private fetchGeneration: number = 0;

  // row block with current and previous cell data in each row object.
  private fetchViewport(): Observable<ICustomGridRow[] | null> {
    const generation: number = this.fetchGeneration;
    const currentViewport: ViewPort = this.currentViewPort$.getValue();
    const endRow: number =
      currentViewport.endRow < this.rowCount ? currentViewport.endRow : this.rowCount;
    const startRow: number =
      currentViewport.startRow >= endRow ? Math.max(0, endRow - this.gridOptions.cacheBlockSize) : currentViewport.startRow;

    if (startRow == null || endRow == null || startRow >= endRow) {
      console.warn('Aborting fetchViewport: startRow and endRow are either null or an empty list would be ' +
        'requested.', {startRow, endRow});
      return of([]);
    }

    return this._tableService.tablePostFetchRows({
        _parameters: [
          this.apiInstance().instanceid,
          startRow,
          endRow,
          currentViewport.startCol,
          currentViewport.endCol,
        ]
      }
    ).pipe(
      catchError(err => {
        this.feedbackService.hide()
        return of(err)
      }),
      map((_: object[]) => {
        if (generation !== this.fetchGeneration) {
          // The cache was reset while this request was in flight — do not write the stale
          // rows into the new cache state.
          return null;
        }
        const apiRows = _ as IApiRow[];
        const rowsThisBlock = this.apiAdapter.createGridRows(apiRows);
        this.viewPortTable.addRowBlock(
          rowsThisBlock,
          currentViewport,
          this.staticColumns
        );
        this.currentViewPortChanged.emit(this.currentViewPort$.getValue());
        return this.viewPortTable.getRowBlock(currentViewport);
      })
    );
  }

  onRowClicked(evt: RowClickedEvent) {
    this.selectedAll = false;
    const event: PointerEvent = evt.event as PointerEvent;

    if (this.gridOptions.rowSelection == 'single') {
      this.selectRowNode(evt.node, evt.rowIndex, false);
      return;
    }

    if (!event.shiftKey) {
      this.selectRowNode(evt.node, evt.rowIndex, event.ctrlKey);
      return;
    }

    // Shift key is pressed
    if (this._startSelectIndex > evt.rowIndex) {
      // Swap start with end if start > end
      this._endSelectIndex = this._startSelectIndex;
      this._startSelectIndex = evt.rowIndex;
    } else {
      this._endSelectIndex = evt.rowIndex;
    }

    const viewport = this.currentViewPort$.getValue()

    patchState(this.state, _ => ({loading: true}));

    // Fetch all rows of the range
    this._tableService.tablePostFetchRows({
      _parameters: [
        this.apiInstance().instanceid,
        this._startSelectIndex,
        this._endSelectIndex + 1,
        viewport.startCol,
        viewport.endCol
      ]
    }).pipe(
      tap((_) => {
        const rows: IApiRow[] = _ as IApiRow[];
        const mapped = this.apiAdapter.createGridRows(rows);
        this.viewPortTable.addRowBlock(
          mapped,
          viewport,
          this.staticColumns
        );
        this.cdr.detach();
        this._rowSelection.setSelection(...rows.filter((r: IApiRow): boolean => r.id != null)
          .map((r: IApiRow): string => r.id));

        this.cdr.reattach();
      }),
      finalize(() => {
        patchState(this.state, _ => ({loading: false}));
      })
    ).subscribe();
  }

  private selectRowNode(node: RowNode, rowIndex: number, ctrlKey: boolean): void {
    this._startSelectIndex = rowIndex;
    if (this.gridOptions.rowSelection === 'single' || !ctrlKey) {
      this.resetSelection();
      if (node?.id) {
        this._rowSelection.setSelection(node.id);
      }
    } else {
      this._rowSelection.toggle(node.id);
    }
  }

  /**
   * When a row is created or deleted, the table should redraw the rows once after finishing all get rows requests.
   * @constructor
   * @private
   */
  private DimensionChanged(): Observable<number> {
    // Every getRow request is invalid when the table dimension changed.
    this.openGetRowReqs$.next(null);
    return merge(this.rowCreated, this.rowDeleted).pipe(
      mergeMap(() => this.openGetRowReqs$.pipe(debounceTime(500))),
      // only redraw rows when the open http requests are zero
      filter((n: number) => {
        return n === 0;
      }),
      tap(() => {
        this.cdr.detectChanges();
        this.gridApi.redrawRows();
      }),
      // only do it once
      take(1)
    );
  }

  /**
   * Initializes the infinite row model data source.
   * @param gridApi
   * @param dataSource
   * @private
   */
  private initializeDataSource(gridApi: GridApi, dataSource: IDatasource) {
    // add data source to grid definition
    gridApi.setDatasource(dataSource);
  }

  refreshInfiniteCache() {
    if (this.gridApi == null) return;
    this.cancel$.next(true);
    this.cancel$.next(false);
    this.resetRowCache();
    this.gridApi.showLoadingOverlay();
    this.gridApi.refreshInfiniteCache();
  }

  private updateRowSpan(colDef: ColDef) {
    if (this.gridApi == null || this.gridApi.getColumnDefs() == null) return;
    colDef = {
      ...colDef, rowSpan: params => {
        if (params.data == null || params.data[params.colDef.field] == null) return 1;
        const grouplength = params.data[params.colDef.field].grouplength;
        console.log(grouplength)
        return grouplength == null ? 1 : grouplength;
      }
    }

    const coldefs = this.gridApi.getColumnDefs()
    if (coldefs == null) return;
    coldefs.forEach((cd: ColDef, i: number) => {
      if (cd.field === colDef.field) {
        coldefs[i] = colDef;
      }
    })

    this.columnDefs = {...coldefs};
  }

  getIsDisabled(action: ITableAction): boolean {
    return (action.disabledIfNoSelection && this._rowSelection.isEmpty()) ||
      (action.isDisabled != undefined && action.isDisabled()) ||
      (action.disabledIfMultiSelection && this._rowSelection.selected.length > 1);
  }

  getAllRows(): ICustomGridRow[] {
    const rowData: ICustomGridRow[] = [];
    this.gridApi?.forEachNode((node: RowNode): void => {
      if (node.data) {
        rowData.push(node.data);
      }
    });
    return rowData;
  }

  getAllRows$(): Observable<ICustomGridRow[]> {
    if (this.openGetRowReqs$.getValue() === 0) {
      return of(this.getAllRows());
    }
    return this.openGetRowReqs$.pipe(
      debounceTime(100),
      filter((n: number): boolean => n === 0),
      take(1),
      map((): ICustomGridRow[] => this.getAllRows())
    );
  }

  onGridScroll(evt: BodyScrollEvent) {
    // Clear the previous timeout to avoid multiple calls within the throttle duration
    clearTimeout(this.scrollThrottleTimeout);

    // Set a new timeout to delay the function execution
    this.scrollThrottleTimeout = setTimeout(() => {
      // Handle the horizontal scroll event here
      if (evt.direction === 'horizontal') {
        this.fitColumns();
      }
    }, 1000); // Adjust the throttle duration as needed (e.g., 1000 ms = 1 second)
  }

  getRowData() {
    const currentViewport = this.currentViewPort$.getValue();
    const endRow =
      currentViewport.endRow < this.rowCount ? currentViewport.endRow : this.rowCount;
    const startRow =
      currentViewport.startRow >= endRow ? Math.max(0, endRow - this.gridOptions.cacheBlockSize) : currentViewport.startRow;
    return this._tableService.tablePostFetchRows(
      {
        _parameters: [
          this.apiInstance().instanceid,
          startRow,
          endRow,
          currentViewport.startCol,
          currentViewport.endCol
        ]
      });
  }

  private setInitialSelection(gridRow: RowNode): void {
    if (gridRow?.id) {
      if (this._rowSelection.isSelected(gridRow.id)) {
        // Already in SelectionModel (e.g., after refreshInfiniteCache without resetSelection):
        // _rowSelection.select() would be a no-op and changed never fires,
        // so restore visual selection directly.
        if (!gridRow.isSelected()) {
          gridRow.setSelected(true);
        }
      } else {
        this.automaticallySetRowSelection.add(gridRow.id);
        this._rowSelection.select(gridRow.id);
      }
    }
  }

  private showPdfOverlay(evt: IPdfBlob, externalData?: boolean) {
    if (!this.pdfOverlayRef || !this.gridApi) return;
    this.gridOptions.paginationAutoPageSize = false;
    this.gridOptions.paginationPageSize = 1;
    const selectedRowIndex = this.selectedRows()[0]?.apiRow?.rowidx || 0;

    // Extra Step to ensure there is a change of page, since this triggers some necessary detections
    if (!evt.externalData) {
      this.gridApi.paginationGoToPage(selectedRowIndex + 1);
      this.gridApi.paginationGoToPage(selectedRowIndex);
    }

    const pdfOverlay = this.pdfOverlayRef.nativeElement;
    const elementRef = this.gridElementRefs.get(0);
    const gridWrapperRef = this.gridWrapperRef.nativeElement;
    const selectedRowElements = elementRef.nativeElement
      .querySelectorAll(`#grid-wrapper :not(.ag-hidden) > .ag-row.ag-row-selected`);
    let rowBottom: number = gridWrapperRef.getBoundingClientRect().top;
    selectedRowElements.forEach((el: HTMLElement) => {
      rowBottom = el.getBoundingClientRect().bottom;
    });

    const offset = rowBottom - gridWrapperRef.getBoundingClientRect().top;
    const paginator = elementRef.nativeElement.querySelector('.ag-paging-panel');
    paginator.style.position = 'absolute';
    paginator.style.top = offset + 'px';
    paginator.style.width = '100%';

    pdfOverlay.style.top = offset + paginator.getBoundingClientRect().height + 'px';
    pdfOverlay.style.height = gridWrapperRef.getBoundingClientRect().height - offset - paginator.getBoundingClientRect().height - 10 + 'px';
    pdfOverlay.style.width = '100%';

    if (evt.externalData === true) return;

    const topRowIndex = this.gridApi.paginationGetCurrentPage();

    // Set the focused cell to the top row of the new page
    this.gridApi.setFocusedCell(topRowIndex, "0");

    // Make the top row selected
    this.gridApi.forEachNode((node: RowNode): void => {
      if (node.rowIndex === topRowIndex && node?.id) {
        this._rowSelection.select(...[node.id]);
      } else {
        this._rowSelection.deselect(...[node.id]);
      }
    });
    this.gridApi.hideOverlay();
  }

  private hidePdfOverlay() {
    if (!this.pdfOverlayRef || !this.gridApi) return;
    const pdfOverlay = this.pdfOverlayRef.nativeElement;

    pdfOverlay.style.height = '0';

    // Define a named function for the event listener
    const handlePaginationChange = (event: any) => {
      if (!this.gridApi) {
        event.api.removeEventListener('paginationChanged', handlePaginationChange);
        return;
      }
      // Retrieve the updated page size when pagination changes
      const pageSize = event.api.paginationGetPageSize();
      const selectedRowIndex = this.selectedRows()[0]?.apiRow?.rowidx;

      if (selectedRowIndex) {
        const newPage = Math.floor(selectedRowIndex / pageSize);
        this.gridApi.paginationGoToPage(newPage);
      }

      // Remove the event listener after it has been executed once
      this.gridApi.removeEventListener('paginationChanged', handlePaginationChange);
    }

    // Add an event listener for the paginationChanged event
    this.gridApi.addEventListener('paginationChanged', handlePaginationChange);

    const paginator = this.elementRef.nativeElement.querySelector('.ag-paging-panel');
    paginator.style.position = 'static';
    this.resetPaginationPageSize();
    this.gridApi.hideOverlay();
  }

  /**
   * Handles the Delete key press event to clear cell content.
   * Only processes events that originate from this table component and when no cell is currently being edited.
   *
   * @param evt - The keyboard event triggered by the Delete key
   */
  @HostListener('keydown.delete', ['$event'])
  onDelete(evt: any): void {
    if (!this.isEventFromThisComponent(evt)) {
      return;
    }
    const focusedCell: CellPosition = this.gridApi.getFocusedCell();
    if (!focusedCell || this.gridApi.getEditingCells().length > 0) return;
    if (this.gridApi.getEditingCells().length) return;

    this.gridApi.startEditingCell({
      rowIndex: focusedCell.rowIndex,
      colKey: focusedCell.column.getColId(),
      charPress: 'delete',
    });
  }

  /**
   * Handles Enter key press events globally to apply and therefore close column filters when pressed while a column
   * filter is open.
   *
   * @param event - The keyboard event containing details about the key press
   */
  @HostListener('window:keydown.enter', ['$event'])
  onEnter(event: KeyboardEvent): void {
    const filterElement: Element = document.querySelector('.ag-filter');
    if (filterElement?.contains(event.target as HTMLElement)) {
      event.preventDefault();
      this.applyColumnFilter(filterElement);
    }
  }

  /**
   * Handles keyboard shortcuts for copy and paste operations within the table.
   * Processes Ctrl+C/Cmd+C for copying cell values and Ctrl+V/Cmd+V for pasting content.
   * Only processes events that originate from this table component.
   *
   * @param event - The keyboard event containing key and modifier information
   */
  @HostListener('keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    if (!this.isEventFromThisComponent(event)) {
      return;
    }

    if (event.key === ' ') {
      event.preventDefault();
      if (this.gridApi.getEditingCells().length > 0) return;
      const focusedCell: CellPosition = this.gridApi.getFocusedCell();
      if (!focusedCell) return;
      const focusedNode: RowNode = this.gridApi.getDisplayedRowAtIndex(focusedCell.rowIndex);
      if (!focusedNode?.id) return;
      this.selectedAll = false;
      this.selectRowNode(focusedNode, focusedCell.rowIndex, event.ctrlKey);
      return;
    }

    // Check for copy (C key + modifier key)
    if ((event.key === 'c' || event.key === 'C') &&
      (event.ctrlKey || event.metaKey)) {
      event.preventDefault();
      this.onCopyValue();
      console.log('Copy shortcut detected');
    }

    // Check for paste (V key + modifier key)
    if ((event.key === 'v' || event.key === 'V') &&
      (event.ctrlKey || event.metaKey)) {
      event.preventDefault();
      navigator.clipboard.readText()
        .then((pastedData: string): void => {
          console.log('Clipboard data:', pastedData);
          this.onPasteValue(pastedData);
        })
        .catch((err: any): void => {
          console.error('Failed to read clipboard: ', err);
          if (this.clipboardService.clipboardValue != null) {
            this.onPasteValue(String(this.clipboardService.clipboardValue));
          }
        });
    }
  }

  private downloadCsv(): Observable<any> {
    return this._tableControlService.tableControlCSVExport(
      {InstanceId: this.apiInstance().instanceid},
      'response',
    ).pipe(
      tap((res: HttpResponse<Blob>) => {
        const fileContent: Blob = res?.body;
        let fileName: string = res.headers.get('content-disposition')?.split('filename=')[1]?.split(';')[0];
        if (fileName) {
          fileName = fileName.substring(1, fileName.length - 1);
        }
        if (fileContent == null) {
          this.feedbackService.setError('Es konnte keine CSV-Datei heruntergeladen werden.');
          return;
        }
        const url: string = URL.createObjectURL(fileContent);
        const a: HTMLAnchorElement = document.createElement('a');
        a.href = url;
        a.download = (this.apiInstance()?.name ? (this.apiInstance()?.name + '_') : '') + (fileName || 'export.csv');
        a.click();
        URL.revokeObjectURL(url);
        this.feedbackService.setNotification('Die CSV-Datei wurde heruntergeladen.');
      })
    );
  }

  uploadCsv(evt: Event): Observable<any> {
    const input = evt.target as HTMLInputElement;
    const file: File = (evt.target as any).files[0];
    let blob: Blob;

    if (file) {
      const reader: FileReader = new FileReader();
      reader.onload = (e: ProgressEvent<FileReader>) => {
        const csvData: string | ArrayBuffer = e.target?.result;
        if (typeof csvData === 'string') {
          blob = new Blob([csvData], {type: 'text/csv'});
        }
        this._tableService.tablePostCsvImport(this.apiInstance().instanceid, blob).subscribe((res) => {
          if (res) {
            this.changeAndFetchInstance();
          }
        });
      };
      reader.readAsText(file);

      // reset value so that change detection can trigger again on the same file if needed
      input.value = ''
    }
    return of(null);
  }

  onTableResize(): void {
    this.setPaginationPageSize();
  }

  onTabActivated(): void {
    this.fitColumns()
  }

  private resetSelection() {
    this.automaticallySetRowSelection = new Set();
    this.savedSelectedRowsIds = [];
    this.gridApi.deselectAll()
    this._rowSelection.clear()
  }

  private isEventFromThisComponent(event: Event): boolean {
    return this.elementRef.nativeElement.contains(event.target as Node);
  }
}
